/**
 * Vista de Eventos para Miembros
 * - Solo lectura: muestra próximos eventos y sus inscripciones
 * - Sin capacidad de crear/editar eventos ni inscribir a otros
 */

import { useMemo } from "react";
import { Card, CardContent, Badge } from "@/components/ui";
import {
  Calendar,
  MapPin,
  Users,
  Loader2,
  CalendarCheck,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { useEvents, usePersonRegistrations } from "@/hooks/useEvents";
import { useAuthStore } from "@/stores/authStore";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import type { EventRegistration } from "@/types";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

// Helper para formatear fecha
const formatDate = (date: Date): string => {
  return date.toLocaleDateString("es-CO", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Formato corto para badges
const formatShortDate = (date: Date): string => {
  return date.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
  });
};

// Estado de inscripción con colores
const getRegistrationStatusInfo = (status: EventRegistration["status"]) => {
  switch (status) {
    case "approved":
      return {
        label: "Aprobada",
        color: "bg-green-100 text-green-700",
        icon: CheckCircle2,
      };
    case "pending":
      return {
        label: "Pendiente",
        color: "bg-yellow-100 text-yellow-700",
        icon: Clock,
      };
    case "waitlist":
      return {
        label: "Lista de espera",
        color: "bg-blue-100 text-blue-700",
        icon: AlertCircle,
      };
    case "rejected":
      return {
        label: "Rechazada",
        color: "bg-red-100 text-red-700",
        icon: XCircle,
      };
    case "cancelled":
      return {
        label: "Cancelada",
        color: "bg-gray-100 text-gray-500",
        icon: XCircle,
      };
    default:
      return {
        label: status,
        color: "bg-gray-100 text-gray-600",
        icon: AlertCircle,
      };
  }
};

export default function MemberEventsView() {
  const { profile } = useAuthStore();
  const { data: events, isLoading: loadingEvents } = useEvents();

  // Obtener inscripciones del miembro actual
  const personId = profile?.personId;
  const { data: myRegistrations, isLoading: loadingRegistrations } =
    usePersonRegistrations(personId);

  // Filtrar solo eventos próximos y publicados
  const upcomingEvents = useMemo(() => {
    if (!events) return [];
    const now = new Date();
    return events
      .filter((e) => {
        const isPublished = [
          "published",
          "registration_closed",
          "in_progress",
        ].includes(e.status);
        const isUpcoming = toSafeDate(e.endDate || e.startDate) >= now;
        return isPublished && isUpcoming;
      })
      .sort(
        (a, b) =>
          toSafeDate(a.startDate).getTime() - toSafeDate(b.startDate).getTime(),
      );
  }, [events]);

  // Mapa de eventId -> inscripción del miembro
  const myRegistrationsByEvent = useMemo(() => {
    const map = new Map<string, EventRegistration>();
    myRegistrations?.forEach((r) => {
      if (["pending", "approved", "waitlist"].includes(r.status)) {
        map.set(r.eventId, r);
      }
    });
    return map;
  }, [myRegistrations]);

  // Eventos donde estoy inscrito (activos)
  const myActiveRegistrations = useMemo(() => {
    return (myRegistrations || []).filter((r) =>
      ["pending", "approved", "waitlist"].includes(r.status),
    );
  }, [myRegistrations]);

  const isLoading = loadingEvents || loadingRegistrations;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">Eventos</h1>
        <p className="text-sm text-gray-500 mt-1">
          Consulta los próximos eventos y el estado de tus inscripciones
        </p>
      </div>

      {/* Mis inscripciones activas */}
      {myActiveRegistrations.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
              <CalendarCheck className="w-4 h-4 text-primary-600" />
              Mis inscripciones ({myActiveRegistrations.length})
            </h2>
            <div className="space-y-2">
              {myActiveRegistrations.map((reg) => {
                const event = events?.find((e) => e.id === reg.eventId);
                const statusInfo = getRegistrationStatusInfo(reg.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <div
                    key={reg.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800 truncate">
                        {event?.name || "Evento"}
                      </p>
                      {event && (
                        <p className="text-xs text-gray-500">
                          {formatDate(toSafeDate(event.startDate))}
                        </p>
                      )}
                    </div>
                    <Badge
                      className={`${statusInfo.color} flex items-center gap-1`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusInfo.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de próximos eventos */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Próximos eventos
        </h2>

        {upcomingEvents.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No hay eventos próximos</p>
              <p className="text-sm">
                Consulta más tarde para ver nuevos eventos
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {upcomingEvents.map((event) => {
              const myReg = myRegistrationsByEvent.get(event.id);
              const startDate = toSafeDate(event.startDate);
              const endDate = event.endDate ? toSafeDate(event.endDate) : null;
              const statusLabel = getEventStatusLabel(event.status);
              const statusColor = getEventStatusColor(event.status);

              return (
                <Card key={event.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Título */}
                        <h3 className="font-semibold text-gray-800 truncate">
                          {event.name}
                        </h3>

                        {/* Fecha y ubicación */}
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>
                              {formatShortDate(startDate)}
                              {endDate && ` - ${formatShortDate(endDate)}`}
                            </span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="truncate">
                                {event.location.city}
                                {event.location.name
                                  ? ` · ${event.location.name}`
                                  : ""}
                              </span>
                            </div>
                          )}
                          {event.registrationConfig?.maxCapacity && (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span>
                                Cupos: {event.registrationConfig.maxCapacity}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Estado del evento */}
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={statusColor}>{statusLabel}</Badge>
                        {myReg && (
                          <Badge
                            className={
                              getRegistrationStatusInfo(myReg.status).color
                            }
                          >
                            {getRegistrationStatusInfo(myReg.status).label}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Descripción */}
                    {event.description && (
                      <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                        {event.description}
                      </p>
                    )}

                    {/* Mensaje si ya está inscrito */}
                    {myReg && (
                      <div className="mt-3 p-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Ya estás inscrito en este evento
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
